/**
 * Terminal sign-in.
 *
 * A POS login is not a website login: the tenant code and the device are fixed
 * properties of the terminal, so both are remembered and the operator only ever
 * types a username and a password. The device id is sent with the credentials
 * because the API refuses a POS login from a device whose `activation_status`
 * is not ACTIVE — that check is the whole point of device binding.
 */

import { useState } from 'react';
import { Button, Card, Input, FormField, ErrorState } from '@sonauto/ui';
import { useAuth } from './AuthProvider';
import { loadDeviceBinding, loadTenantCode, saveTenantCode } from '../lib/storage';
import { errorMessage } from '../lib/hooks';

export function LoginScreen(): React.JSX.Element {
  const { api, setTokens } = useAuth();
  const binding = loadDeviceBinding();
  const [tenantCode, setTenantCode] = useState(() => loadTenantCode());
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const result = await api.auth.login({
        tenantCode: tenantCode.trim(),
        username: username.trim(),
        password,
        app: 'POS',
        branchId: binding?.branchId ?? null,
        stationId: binding?.stationId ?? null,
        // Sent only when this terminal has been bound. An unbound terminal can
        // still sign in (the API skips the device check when deviceId is
        // absent) — that is deliberate, so a manager can log in to pair it.
        deviceId: binding?.deviceId ?? null,
      });
      saveTenantCode(tenantCode.trim());
      setTokens({
        accessToken: result.accessToken,
        refreshToken: result.refreshToken,
        expiresAt: result.expiresAt,
      });
      if (result.mustChangePassword) {
        setError('Tài khoản cần đổi mật khẩu. Vui lòng đổi mật khẩu trong ứng dụng Quản trị trước khi dùng POS.');
      }
    } catch (cause: unknown) {
      setError(errorMessage(cause));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-full items-center justify-center bg-canvas p-4">
      <Card className="w-full max-w-md" title="SonAuto POS" description="Đăng nhập ca làm việc">
        <form onSubmit={(e) => void submit(e)} className="space-y-4">
          <FormField label="Mã tổ chức" required>
            {(field) => (
              <Input
                {...field}
                value={tenantCode}
                onChange={(e) => setTenantCode(e.target.value)}
                autoComplete="organization"
                required
              />
            )}
          </FormField>
          <FormField label="Tên đăng nhập" required>
            {(field) => (
              <Input
                {...field}
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                autoComplete="username"
                autoFocus
                required
              />
            )}
          </FormField>
          <FormField label="Mật khẩu" required>
            {(field) => (
              <Input
                {...field}
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                required
              />
            )}
          </FormField>

          {error !== null ? <ErrorState title="Không đăng nhập được" message={error} /> : null}

          <Button type="submit" size="touch" fullWidth loading={busy}>
            Đăng nhập
          </Button>

          <p className="text-flow text-center text-xs text-fg-subtle">
            {binding === null
              ? 'Thiết bị chưa được gán. Sau khi đăng nhập, quản lý có thể kích hoạt thiết bị này.'
              : `Thiết bị: ${binding.deviceName}${binding.deviceMode === null ? '' : ` · ${binding.deviceMode}`}`}
          </p>
        </form>
      </Card>
    </div>
  );
}
